import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from "@/store/useAuthStore.jsx";
import authApi from "@services/authApi.jsx";

// 토큰의 만료 시간 확인 함수
const isTokenExpired = (token) => {
    if (!token) return true;

    try {
        // JWT 토큰 파싱
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(
            decodeURIComponent(
                window.atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
            )
        );

        // 현재 시간과 만료 시간 비교 (exp는 초 단위)
        const now = Math.floor(Date.now() / 1000);
        return !payload.exp || payload.exp < now;
    } catch (error) {
        console.error('토큰 검증 중 오류 발생:', error);
        return true; // 오류 발생 시 만료된 것으로 처리
    }
};

// 액세스 토큰 갱신 함수
const refreshAccessToken = async () => {
    try {
        // 리프레시 토큰으로 새 액세스 토큰 요청
        const response = await authApi.refreshToken();
        return response.data;
    } catch (error) {
        console.error('토큰 갱신 중 오류 발생:', error);
        throw error;
    }
};

// 인증이 필요한 컴포넌트를 감싸는 보호 컴포넌트
export default function ProtectedRoute({ children }) {
    const navigate = useNavigate();
    const { isAuthenticated, syncAuthState, logout, updateTokens } = useAuthStore();

    useEffect(() => {
        const checkAndRefreshTokens = async () => {
            // 액세스 토큰 가져오기
            const accessToken = localStorage.getItem('ab_at');
            const refreshToken = localStorage.getItem('ab_rt');

            if( !accessToken && !refreshToken ) {
                return navigate('/login', { replace: true })
            }

            // refresh 토큰 확인
            if( isTokenExpired(refreshToken) ) {
                return navigate('/login', { replace: true })
            }

            if (isTokenExpired(accessToken)) {
                console.log('액세스 토큰이 만료되었습니다. 토큰을 갱신합니다.');

                try {
                    // 액세스 토큰 갱신 시도
                    const tokenResponse = await refreshAccessToken();

                    // 새 토큰 저장
                    updateTokens({
                        accessToken: tokenResponse.accessToken,
                        refreshToken: tokenResponse.refreshToken
                    });

                    // 상태 동기화
                    syncAuthState();
                } catch (error) {
                    console.error('토큰 갱신 실패:', error);
                    // 갱신 실패 시 로그아웃
                    localStorage.removeItem('ab_at');
                    localStorage.removeItem('ab_rt');
                    logout();
                    navigate('/login', { replace: true });
                    return;
                }
            }

            // 인증 상태 동기화
            const isLoggedIn = syncAuthState();

            // 토큰이 있지만 상태 동기화 실패 시
            if (!isLoggedIn) {
                navigate('/login', { replace: true });
            }
        };

        checkAndRefreshTokens();
    }, [navigate, syncAuthState, logout, updateTokens]);

    // 인증된 경우에만 자식 컴포넌트 렌더링
    return isAuthenticated ? children : null;
}